/** Strongly-typed event map + a tiny synchronous event bus. Domain modules emit
 * these; the client subscribes for UI/audio reactions. Zero `any`. */
import type { EntityId, Tick } from '../ids.js';

export interface GameEventMap {
  'sim:tick': { readonly tick: Tick };
  'entity:spawned': { readonly entity: EntityId };
  'entity:destroyed': { readonly entity: EntityId };
  'map:generated': { readonly width: number; readonly height: number; readonly seed: number };
}

export type GameEventName = keyof GameEventMap;
export type GameEventListener<K extends GameEventName> = (payload: GameEventMap[K]) => void;

export class EventBus {
  private readonly listeners = new Map<GameEventName, Set<(payload: never) => void>>();

  on<K extends GameEventName>(name: K, listener: GameEventListener<K>): () => void {
    let set = this.listeners.get(name);
    if (!set) {
      set = new Set();
      this.listeners.set(name, set);
    }
    set.add(listener);
    return () => {
      set?.delete(listener);
    };
  }

  emit<K extends GameEventName>(name: K, payload: GameEventMap[K]): void {
    const set = this.listeners.get(name);
    if (!set) return;
    for (const listener of set) {
      (listener as GameEventListener<K>)(payload);
    }
  }

  clear(): void {
    this.listeners.clear();
  }
}
